import * as z from 'zod/mini';

// MonitoringFrequency enum schema
export const MonitoringFrequencySchema = z.enum([
  '30min',
  'hourly', 
  'daily',
  'weekly'
]);

// Area coordinates schema (matches API format)
export const StoredAreaCoordinatesSchema = z.object({
  viewport_width: z.number(),
  viewport_height: z.number(),
  percent_x: z.number(),
  percent_y: z.number(),
  percent_width: z.number(),
  percent_height: z.number(),
});

// URL schema with comprehensive validation
export const URLSchema = z.object({
  id: z.string(),
  user_id: z.string(),
  url: z.string().check(
    z.regex(/^https?:\/\/.+/),
    z.minLength(10)
  ),
  reference_screenshot_id: z.string(),
  last_screenshot: z.string(),
  next_screenshot: z.string(),
  active: z.boolean(),
  area_coordinates: z.nullable(StoredAreaCoordinatesSchema),
  created_at: z.string(),
  updated_at: z.string()
});

// Screenshot schema
export const ScreenshotSchema = z.object({
  id: z.string(),
  url_id: z.string(),
  user_id: z.string(),
  path: z.string().check(z.minLength(1)),
  is_reference: z.boolean(),
  timestamp: z.string().check(z.iso.datetime()),
  created_at: z.string().check(z.iso.datetime())
});

// Comparison schema
export const ComparisonSchema = z.object({
  id: z.string(),
  url_id: z.string(),
  user_id: z.string(),
  reference_screenshot_id: z.string(),
  new_screenshot_id: z.string(),
  diff_image_path: z.string(),
  change_percentage: z.number().check(
    z.gte(0),
    z.lte(100)
  ),
  email_sent: z.boolean(),
  created_at: z.string().check(z.iso.datetime())
});

// User schema with email validation
export const UserSchema = z.object({
  id: z.string(),
  email: z.string().check(
    z.regex(/^[^\s@]+@[^\s@]+\.[^\s@]+$/),
    z.toLowerCase(),
    z.trim()
  ),
  verification_hash: z.string(),
  is_verified: z.boolean(),
  verified_at: z.nullable(z.string().check(z.iso.datetime())),
  created_at: z.string().check(z.iso.datetime()),
  updated_at: z.string().check(z.iso.datetime())
});

// Job type and status enums
export const JobTypeSchema = z.enum([
  'screenshot',
  'comparison',
  'email'
]);

export const JobStatusSchema = z.enum([
  'pending',
  'processing',
  'completed',
  'failed',
  'retry'
]);

// Job schema with type and status validation
export const JobSchema = z.object({
  id: z.string(),
  type: JobTypeSchema,
  payload: z.string(),
  status: JobStatusSchema,
  attempts: z.number().check(
    z.nonnegative(),
    z.multipleOf(1)
  ),
  max_attempts: z.number().check(
    z.positive(),
    z.multipleOf(1)
  ),
  last_error: z.nullable(z.string()),
  created_at: z.string().check(z.iso.datetime()),
  updated_at: z.string().check(z.iso.datetime()),
  locked_until: z.string().check(z.iso.datetime())
});

// Email schema
export const EmailSchema = z.object({
  id: z.string(),
  user_id: z.string(),
  url_id: z.string(),
  comparison_id: z.string(),
  to_email: z.string().check(
    z.regex(/^[^\s@]+@[^\s@]+\.[^\s@]+$/),
    z.toLowerCase(),
    z.trim()
  ),
  subject: z.string().check(z.minLength(1)),
  body: z.string().check(z.minLength(1)),
  sent: z.boolean(),
  created_at: z.string().check(z.iso.datetime()),
  sent_at: z.nullable(z.string().check(z.iso.datetime()))
});

// Type inference exports
export type MonitoringFrequency = z.infer<typeof MonitoringFrequencySchema>;
export type StoredAreaCoordinates = z.infer<typeof StoredAreaCoordinatesSchema>;
export type URL = z.infer<typeof URLSchema>;
export type Screenshot = z.infer<typeof ScreenshotSchema>;
export type Comparison = z.infer<typeof ComparisonSchema>;
export type User = z.infer<typeof UserSchema>;
export type JobType = z.infer<typeof JobTypeSchema>;
export type JobStatus = z.infer<typeof JobStatusSchema>;
export type Job = z.infer<typeof JobSchema>;
export type Email = z.infer<typeof EmailSchema>;